import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import NoteRenderer from "@/components/NoteRenderer";
import { ArrowLeft, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import ReviewsList from "@/components/ReviewsList";

export default function PublicNote() {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/notes/${slug}`);
        setNote(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Note not found");
      }
    })();
  }, [slug]);

  // Inject per-note OG + Twitter meta tags so /share/{slug} previews with a
  // custom card + topic/subject on WhatsApp / X / LinkedIn / iMessage / etc.
  useEffect(() => {
    if (!note) return;
    const n = note.notes || {};
    const topic = (n.topic || note.title || "Study Notes").trim();
    const subject = (n.subject || "general").replace(/_/g, " ");
    const summary = (n.one_line_summary || "").trim();
    const url = `${window.location.origin}/share/${slug}`;
    const imgUrl = `${API}/og/note/${slug}.png`;
    const title = `${topic} · Smart S Notes`;
    const desc = summary || `1-page ${subject} notes — exam-ready in under a minute.`;

    document.title = title;

    const upsert = (kind, key, val) => {
      const attr = kind === "property" ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    upsert("name",     "description",         desc);
    upsert("property", "og:title",            title);
    upsert("property", "og:description",      desc);
    upsert("property", "og:url",              url);
    upsert("property", "og:image",            imgUrl);
    upsert("property", "og:image:width",      "1200");
    upsert("property", "og:image:height",     "630");
    upsert("property", "og:image:alt",        topic);
    upsert("property", "og:type",             "article");
    upsert("name",     "twitter:card",        "summary_large_image");
    upsert("name",     "twitter:title",       title);
    upsert("name",     "twitter:description", desc);
    upsert("name",     "twitter:image",       imgUrl);
    upsert("name",     "twitter:image:alt",   topic);
  }, [note, slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F0F9FF]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16">
          <p data-testid="public-error" className="font-mono text-sm text-[#FF3333]">{error}</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest underline">
            <ArrowLeft className="w-3 h-3" /> Go to Smart S Notes
          </Link>
        </main>
      </div>
    );
  }

  if (!note) {
    return (
      <div className="min-h-screen bg-[#F0F9FF]">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-16">
          <div data-testid="public-loading" className="font-mono text-sm text-zinc-500">Loading shared note…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            / Shared by {note.author_name}
          </p>
          <Link
            data-testid="public-cta"
            to="/register"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest px-4 py-2 bg-[#3B82F6] text-white btn-brutal"
          >
            <Sparkles className="w-3.5 h-3.5" /> Make your own
          </Link>
        </div>
        <div data-testid="public-note-body" className="bg-white border border-[#BFDBFE] p-8 sm:p-12">
          <NoteRenderer notes={note.notes} />
        </div>

        <div className="mt-12">
          <ReviewsList
            scope="note"
            targetId={note.id}
            limit={12}
            heading="Reader reviews"
            emptyLabel="No reviews yet — be the first to rate this note."
            compact
          />
        </div>
      </main>
    </div>
  );
}
